"use client"
import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import Image from "next/image"
import { sellerDisplayName } from "@/lib/accounts/display-name"
import SellerLink from "@/components/SellerLink"

/* ── data ─────────────────────────────────────────────────────────── */
const CITIES = ["Harare", "Bulawayo", "Mutare", "Gweru", "Victoria Falls", "Masvingo", "Kwekwe", "Chinhoyi", "Bindura"]

type Category = "land" | "residential" | "commercial"
const CATEGORIES: { id: Category; label: string; icon: string; desc: string; types: string[] }[] = [
  { id: "land",        label: "Land",        icon: "🏞️", desc: "Stands, plots and agricultural land for sale",          types: ["LAND"] },
  { id: "residential", label: "Residential", icon: "🏠", desc: "Houses, apartments and cluster homes for sale",         types: ["HOUSE", "APARTMENT", "CLUSTER"] },
  { id: "commercial",  label: "Commercial",  icon: "🏢", desc: "Offices, retail, industrial and mixed-use properties",  types: ["COMMERCIAL"] },
]

/* ── helpers ──────────────────────────────────────────────────────── */
function fmt(n: number) {
  if (n >= 1_000_000) return "US$" + (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "M"
  if (n >= 1_000)     return "US$" + (n / 1_000).toFixed(0) + "K"
  return "US$" + n.toLocaleString()
}

/* ── sub-components ───────────────────────────────────────────────── */
function ListingCard({ l }: { l: any }) {
  const photo = l.photos?.[0]?.url
  const score = l.trustScore?.totalScore ?? 0
  const typeLabel: Record<string, string> = { HOUSE: "House", APARTMENT: "Apartment", CLUSTER: "Cluster", LAND: "Land", COMMERCIAL: "Commercial" }
  return (
    <Link href={`/listings/${l.id}`} style={{ display: "block", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden", textDecoration: "none", transition: "box-shadow 0.2s" }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = "var(--shadow-lg)")}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}>
      <div style={{ height: 180, background: photo ? undefined : "var(--cream-warm)", position: "relative" }}>
        {photo && <Image src={photo} alt={l.title ?? "Listing"} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
        {!photo && <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 40 }}>🏠</div>}
        <div style={{ position: "absolute", top: 10, left: 10, background: "var(--navy)", color: "var(--cream)", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 6 }}>
          {typeLabel[l.propertyType] ?? l.propertyType}
        </div>
        {score >= 70 && (
          <div style={{ position: "absolute", top: 10, right: 10, background: "var(--gold)", color: "var(--navy)", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 6 }}>
            ✓ Trust {score}
          </div>
        )}
      </div>
      <div style={{ padding: "16px 18px" }}>
        <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{fmt(l.price)}</div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>{l.suburb}, {l.city}</div>
        <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600, marginBottom: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.title}</div>
        {(l.beds || l.baths || l.floorArea) && (
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--muted)" }}>
            {l.beds      && <span>🛏 {l.beds} bed{l.beds !== 1 ? "s" : ""}</span>}
            {l.baths     && <span>🚿 {l.baths} bath{l.baths !== 1 ? "s" : ""}</span>}
            {l.floorArea && <span>📐 {l.floorArea}m²</span>}
          </div>
        )}
        {l.agent?.name && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>Agent: <SellerLink agent={l.agent} nested style={{ color: "var(--gold-deep)", fontWeight: 600 }}>{sellerDisplayName(l.agent)}</SellerLink></div>}
      </div>
    </Link>
  )
}

function ProjectCard({ p }: { p: any }) {
  const completionYear = p.completionDate ? new Date(p.completionDate).getFullYear() : null
  return (
    <Link href={`/developers/${p.id}`} style={{ display: "block", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden", textDecoration: "none", transition: "box-shadow 0.2s" }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = "var(--shadow-lg)")}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}>
      <div style={{ height: 160, background: p.coverImage ? undefined : "var(--navy)", position: "relative" }}>
        {p.coverImage && <Image src={p.coverImage} alt={p.name ?? "Development"} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
        {!p.coverImage && <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 40 }}>🏗️</div>}
      </div>
      <div style={{ padding: "16px 18px" }}>
        <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{p.name}</div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 10 }}>{p.suburb}, {p.city}</div>
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--navy)", flexWrap: "wrap" }}>
          {p.availableUnits > 0 && <span style={{ fontWeight: 700, color: "var(--gold-deep)" }}>{p.availableUnits} unit{p.availableUnits !== 1 ? "s" : ""} available</span>}
          {p.minPrice != null && <span>{fmt(p.minPrice)}{p.maxPrice != null && p.maxPrice !== p.minPrice ? ", " + fmt(p.maxPrice) : ""}</span>}
          {completionYear && <span>Est. {completionYear}</span>}
        </div>
      </div>
    </Link>
  )
}

function Skeleton() {
  return (
    <div style={{ background: "var(--cream-warm)", borderRadius: 16, overflow: "hidden", border: "1px solid var(--line)" }}>
      <div style={{ height: 180, background: "var(--line)", animation: "pulse 1.5s infinite" }} />
      <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ height: 22, width: "50%", background: "var(--line)", borderRadius: 6, animation: "pulse 1.5s infinite" }} />
        <div style={{ height: 14, width: "35%", background: "var(--line)", borderRadius: 6, animation: "pulse 1.5s infinite" }} />
        <div style={{ height: 14, width: "80%", background: "var(--line)", borderRadius: 6, animation: "pulse 1.5s infinite" }} />
      </div>
    </div>
  )
}

/* ── main page ────────────────────────────────────────────────────── */
export default function DiasporaPage() {
  const searchRef = useRef<HTMLDivElement>(null)

  const [category, setCategory] = useState<Category | null>(null)
  const [city,     setCity]     = useState("")
  const [suburb,   setSuburb]   = useState("")
  const [suburbs,  setSuburbs]  = useState<string[]>([])
  const [listings, setListings] = useState<any[]>([])
  const [projects, setProjects] = useState<any[]>([])
  const [loading,  setLoading]  = useState(false)
  const [projLoad, setProjLoad] = useState(false)
  const [fetched,  setFetched]  = useState(false)

  /* fetch available suburbs from live listings whenever category or city changes */
  useEffect(() => {
    if (!category || !city) { setSuburbs([]); return }
    const cat = CATEGORIES.find(c => c.id === category)!
    const params = new URLSearchParams({
      distinct:     "suburb",
      priceType:    "SALE",
      propertyType: cat.types.join(","),
      city,
    })
    fetch("/api/listings?" + params)
      .then(r => r.json())
      .then(d => setSuburbs((d.data ?? []).map((s: { suburb: string }) => s.suburb)))
      .catch(() => setSuburbs([]))
  }, [category, city])

  /* fetch listings whenever category / city / suburb changes */
  useEffect(() => {
    if (!category) return
    const cat = CATEGORIES.find(c => c.id === category)!
    const params = new URLSearchParams({
      priceType:    "SALE",
      propertyType: cat.types.join(","),
      pageSize:     "12",
      sort:         "trust",
    })
    if (city)   params.set("city",   city)
    if (suburb) params.set("suburb", suburb)

    setLoading(true)
    setFetched(false)
    fetch("/api/listings?" + params)
      .then(r => r.json())
      .then(d => { setListings(d.data ?? []); setFetched(true) })
      .catch(() => { setListings([]); setFetched(true) })
      .finally(() => setLoading(false))
  }, [category, city, suburb])

  /* fetch developer projects (all), filter client-side by city */
  useEffect(() => {
    setProjLoad(true)
    fetch("/api/developers")
      .then(r => r.json())
      .then(d => {
        const all: any[] = d.data ?? []
        setProjects(city ? all.filter(p => p.city.toLowerCase() === city.toLowerCase()) : all)
      })
      .catch(() => setProjects([]))
      .finally(() => setProjLoad(false))
  }, [city])

  function pickCategory(c: Category) {
    setCategory(c)
    setCity("")
    setSuburb("")
    setFetched(false)
    setTimeout(() => searchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50)
  }

  function seeAllUrl() {
    const cat = CATEGORIES.find(c => c.id === category)
    const p = new URLSearchParams({ priceType: "SALE" })
    if (cat)    p.set("propertyType", cat.types[0])
    if (city)   p.set("city",   city)
    if (suburb) p.set("suburb", suburb)
    return "/listings?" + p
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section style={{ background: "var(--navy)", padding: "80px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", textAlign: "center" }}>
          <span className="eyebrow" style={{ background: "rgba(200,150,62,0.15)", borderColor: "rgba(200,150,62,0.3)", color: "var(--gold-soft)" }}>For the Zimbabwe diaspora</span>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: "clamp(36px,5vw,56px)", fontWeight: 700, color: "var(--cream)", margin: "20px auto 20px", maxWidth: 780, lineHeight: 1.1 }}>
            Your stake in Zimbabwe. <em style={{ color: "var(--gold)" }}>Done right, from wherever you are.</em>
          </h1>
          <p style={{ color: "rgba(253,251,247,0.8)", fontSize: 18, lineHeight: 1.7, maxWidth: 620, margin: "0 auto 36px" }}>
            Zimbabwe real estate is USD-priced, title-deed secured, and one of the most direct ways to build wealth back home. Every agent on this platform is KYC-verified and EACZ-registered, no anonymous sellers, no off-plan projects without paperwork.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => searchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="btn-primary" style={{ fontSize: 16, padding: "16px 32px", cursor: "pointer", border: "none" }}>
              Browse properties ↓
            </button>
            <Link href="/how-it-works" className="btn-ghost" style={{ fontSize: 16, padding: "16px 32px", color: "var(--cream)", border: "1px solid rgba(253,251,247,0.3)" }}>
              How it works
            </Link>
          </div>
          <div style={{ display: "flex", gap: 48, justifyContent: "center", marginTop: 52, flexWrap: "wrap" }}>
            {[
              { val: "100%", label: "of agents KYC & EACZ verified" },
              { val: "USD",  label: "All prices, no currency risk" },
              { val: "Free", label: "Conveyancer matching included" },
            ].map(s => (
              <div key={s.val} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--gold)" }}>{s.val}</div>
                <div style={{ fontSize: 13, color: "rgba(253,251,247,0.6)", marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Zimbabwe strip ────────────────────────────────────── */}
      <div style={{ background: "var(--cream-warm)", borderBottom: "1px solid var(--line)", padding: "40px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <p style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "var(--navy)", fontWeight: 600 }}>Why buy in Zimbabwe right now?</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 24 }}>
            {[
              { icon: "💵", title: "Priced in US dollars", body: "No ZWG exposure. Land and property are bought and sold in USD, the same currency you earn abroad." },
              { icon: "📜", title: "Title deeds are real", body: "Zimbabwe's Deeds Registry is centralised and searchable. A clean Deeds Office search gives you real legal ownership, internationally recognised." },
              { icon: "📈", title: "Demand is rising", body: "Harare and Bulawayo are experiencing a residential shortage. Rental yields of 8-12% are common in Borrowdale, Highlands, and Suburbs." },
              { icon: "🏠", title: "Diaspora is the market", body: "More than half of Zimbabwe's property transactions now involve diaspora buyers. The market has built itself around you." },
            ].map(r => (
              <div key={r.title} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span style={{ fontSize: 28, flexShrink: 0, marginTop: 2 }}>{r.icon}</span>
                <div>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{r.title}</div>
                  <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.6 }}>{r.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Country cards ─────────────────────────────────────────── */}
      <div className="section" style={{ paddingBottom: 0 }}>
        <p style={{ textAlign: "center", fontFamily: "Fraunces, serif", fontSize: 18, color: "var(--navy)", fontWeight: 600, marginBottom: 24 }}>Buying from abroad? We support you wherever you are.</p>
        <div className="mob-grid-4" style={{ gap: 16, marginBottom: 48 }}>
          {[
            { flag: "🇬🇧", country: "United Kingdom",  sub: "Wise & bank transfers · Agents respond during UK hours · HMRC-aware conveyancers" },
            { flag: "🇿🇦", country: "South Africa",    sub: "Same timezone · FNB & Nedbank USD transfers · Border runner services available" },
            { flag: "🇦🇺", country: "Australia",       sub: "Bank wire & Wise accepted · Weekend video tours · AEST-friendly agents" },
            { flag: "🌍",   country: "Everywhere else", sub: "USD standard pricing · WhatsApp & Zoom viewings · Any timezone supported" },
          ].map((c, i) => (
            <button key={c.country}
              onClick={() => searchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="reveal-scale"
              style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 24, textAlign: "center", display: "block", cursor: "pointer", width: "100%", transitionDelay: `${i * 80}ms`, fontFamily: "inherit" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>{c.flag}</div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>{c.country}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>{c.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Search funnel ─────────────────────────────────────────── */}
      <div ref={searchRef} id="search" style={{ scrollMarginTop: 80 }} className="section">
        <div style={{ marginBottom: 28 }}>
          <span className="eyebrow">Verified sale properties</span>
          <h2 className="section-title" style={{ marginTop: 12, marginBottom: 8 }}>What does your investment look like?</h2>
          <p className="section-sub">Purchase-ready listings only, all prices in USD, all agents EACZ-registered.</p>
        </div>

        {/* ── Step 1: Category ───────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 32 }}>
          {CATEGORIES.map(cat => {
            const active = category === cat.id
            return (
              <button key={cat.id} onClick={() => pickCategory(cat.id)}
                style={{ background: active ? "var(--navy)" : "var(--cream)", border: `2px solid ${active ? "var(--navy)" : "var(--line)"}`, borderRadius: 16, padding: "28px 20px", textAlign: "center", cursor: "pointer", transition: "all 0.18s", fontFamily: "inherit" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>{cat.icon}</div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: active ? "var(--cream)" : "var(--navy)", marginBottom: 6 }}>{cat.label}</div>
                <div style={{ fontSize: 13, color: active ? "rgba(253,251,247,0.7)" : "var(--muted)", lineHeight: 1.4 }}>{cat.desc}</div>
              </button>
            )
          })}
        </div>

        {/* ── Step 2: City + Suburb ─────────────────────────────── */}
        {category && (
          <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: "24px 28px", marginBottom: 32 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>
              Narrow by location <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
            </p>
            {/* City pills */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, marginBottom: 10 }}>City</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {CITIES.map(c => (
                  <button key={c} onClick={() => { setCity(city === c ? "" : c); setSuburb("") }}
                    style={{ padding: "7px 16px", borderRadius: 20, border: `1.5px solid ${city === c ? "var(--navy)" : "var(--line)"}`, background: city === c ? "var(--navy)" : "var(--cream)", color: city === c ? "var(--cream)" : "var(--navy)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            {/* Suburb pills */}
            {city && suburbs.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, marginBottom: 10 }}>Suburb in {city}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {suburbs.map(s => (
                    <button key={s} onClick={() => setSuburb(suburb === s ? "" : s)}
                      style={{ padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${suburb === s ? "var(--gold)" : "var(--line)"}`, background: suburb === s ? "rgba(200,150,62,0.12)" : "var(--cream)", color: "var(--navy)", fontSize: 12, fontWeight: suburb === s ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Listings ──────────────────────────────────── */}
        {category && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--navy)" }}>
                {CATEGORIES.find(c => c.id === category)?.label} for sale
                {city ? ` in ${suburb || city}` : ", Zimbabwe"}
              </h3>
              {fetched && listings.length > 0 && (
                <Link href={seeAllUrl()} style={{ fontSize: 13, fontWeight: 700, color: "var(--gold-deep)", textDecoration: "none" }}>
                  See all results →
                </Link>
              )}
            </div>

            {loading ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20, marginBottom: 48 }}>
                {[...Array(6)].map((_, i) => <Skeleton key={i} />)}
              </div>
            ) : listings.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20, marginBottom: 48 }}>
                {listings.map(l => <ListingCard key={l.id} l={l} />)}
              </div>
            ) : fetched ? (
              <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: "40px 24px", textAlign: "center", marginBottom: 48 }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, color: "var(--navy)", marginBottom: 8 }}>No listings found</div>
                <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 20 }}>
                  {suburb ? `No ${CATEGORIES.find(c => c.id === category)?.label.toLowerCase()} listings in ${suburb} yet. ` : ""}
                  Try a different city or remove the suburb filter.
                </div>
                <Link href={seeAllUrl()} className="btn-navy" style={{ fontSize: 14 }}>
                  Browse all {CATEGORIES.find(c => c.id === category)?.label.toLowerCase()} listings
                </Link>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* ── Trust guarantee strip ─────────────────────────────────── */}
      <div style={{ background: "var(--cream-warm)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", padding: "48px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <p style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "var(--navy)", fontWeight: 600, marginBottom: 6 }}>Our guarantee to every diaspora buyer</p>
            <p style={{ fontSize: 14, color: "var(--muted)" }}>What we check, and where your own conveyancer still matters.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 24 }}>
            {[
              { icon: "✅", title: "Seller identity verified", body: "No listing goes live until our team has checked the seller's national ID and compared their selfie with the ID photo. Agents also give their EAC licence number." },
              { icon: "🔒", title: "Title deed checks", body: "Sellers can upload the title deed privately. When our team has checked it matches the property, the listing shows \"Title deed checked\". Look for it, and still have a conveyancer search the Deeds Office." },
              { icon: "🏗️", title: "Development documents verified", body: "Developers upload their building permit, parent title deed, and compliance certificate. A project only earns its green compliance badges once our team has checked each document." },
              { icon: "⚖️", title: "Free conveyancer matching", body: "We connect you with a verified Zimbabwe property lawyer at no charge. They do the Deeds Office search so you know before you pay." },
            ].map(g => (
              <div key={g.title} style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 14, padding: "22px 20px" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{g.icon}</div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{g.title}</div>
                <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.65 }}>{g.body}</div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: "center", fontSize: 12, color: "var(--muted)", marginTop: 24 }}>
            All agents registered with the <strong>Estate Agents Council of Zimbabwe (EACZ)</strong>. Development compliance documents are <strong>checked before badges are shown</strong>.
          </p>
        </div>
      </div>

      {/* ── Developments ──────────────────────────────────────────── */}
      <div className="section">
        <span className="eyebrow">Registered Developments</span>
        <h2 className="section-title" style={{ marginTop: 12, marginBottom: 8 }}>
          New developments {city ? `in ${city}` : "across Zimbabwe"}
        </h2>
        <p className="section-sub" style={{ marginBottom: 32 }}>
          Every project below shows exactly which compliance documents have been verified, so you can judge it from abroad.
        </p>

        {projLoad ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} />)}
          </div>
        ) : projects.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20 }}>
            {projects.map(p => <ProjectCard key={p.id} p={p} />)}
          </div>
        ) : (
          <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🏗️</div>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, color: "var(--navy)", marginBottom: 6 }}>
              {city ? `No registered developments in ${city} yet` : "No developments listed yet"}
            </div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>Check back soon, new projects are added regularly.</div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 32 }}>
          <Link href="/developers" className="btn-navy" style={{ fontSize: 14 }}>View all developments →</Link>
        </div>
      </div>

      {/* ── Reassurance strip ─────────────────────────────────────── */}
      <div style={{ background: "var(--navy)", padding: "48px 24px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
          <p style={{ fontFamily: "Fraunces, serif", fontSize: "clamp(20px,3vw,28px)", fontWeight: 600, color: "var(--cream)", lineHeight: 1.4, marginBottom: 16 }}>
            "The biggest property mistake diaspora buyers make is trusting a WhatsApp contact over a verified agent."
          </p>
          <p style={{ fontSize: 14, color: "rgba(253,251,247,0.55)", marginBottom: 28 }}>
            — Repeated advice from Zimbabwe property lawyers to diaspora clients, every year. We built Zim Estates so you don't have to learn this the hard way.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <Link href="/services" className="btn-primary" style={{ fontSize: 14 }}>Find a verified conveyancer</Link>
            <Link href="/contact" style={{ fontSize: 14, padding: "12px 24px", border: "1px solid rgba(253,251,247,0.3)", borderRadius: 10, color: "var(--cream)", textDecoration: "none", fontWeight: 600 }}>Talk to our team</Link>
          </div>
        </div>
      </div>

      {/* ── How to buy guide ──────────────────────────────────────── */}
      <div className="section" style={{ background: "var(--cream-warm)" }}>
        <h2 className="section-title" style={{ marginBottom: 8 }}>How to buy from abroad, <em>safely</em>.</h2>
        <p className="section-sub" style={{ marginBottom: 40 }}>A step-by-step process built for buyers who cannot be there in person.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 0, maxWidth: 860 }}>
          {[
            { num: "1", title: "Search verified listings only",           desc: "Every listing has a Trust Score based on ID verification, title deed status, and photo checks. Filter by title-deed verified to see only the most verified properties.", cta: { href: "/listings?titleDeedVerified=true", label: "Browse verified listings" } },
            { num: "2", title: "Request a live video tour",               desc: "Every agent offers video walkthroughs via WhatsApp or Zoom. Ask them to show you the title deed, the stand beacon, and the borehole during the walkthrough. Never accept pre-recorded video only.", cta: { href: "/listings", label: "Find a property to tour" } },
            { num: "3", title: "Appoint a verified conveyancer",          desc: "Do not pay a cent without a conveyancer. Use our directory to find a verified Zimbabwe property lawyer. They will run a Deeds Office search on the property.", cta: { href: "/conveyancers", label: "Find a conveyancer" } },
            { num: "4", title: "Set up Power of Attorney carefully",      desc: "If you need someone in Zimbabwe to sign documents on your behalf, you need a Special Power of Attorney for that specific transaction only.", cta: { href: "/buyers-guide", label: "Read the buyers guide" } },
            { num: "5", title: "Pay only after due diligence is complete", desc: "Never pay a deposit before your conveyancer confirms the Deeds Office search is clean. Pay into the conveyancer trust account, never directly to an agent.", cta: { href: "/trust-centre", label: "Read the safety guide" } },
          ].map((step, i) => (
            <div key={step.num} className="reveal" style={{ display: "flex", gap: 20, padding: "28px 0", borderBottom: "1px solid var(--line)", transitionDelay: `${i * 70}ms` }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--navy)", color: "var(--cream)", display: "grid", placeItems: "center", fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, flexShrink: 0, marginTop: 2 }}>{step.num}</div>
              <div>
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--navy)", marginBottom: 6 }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.6, marginBottom: 10 }}>{step.desc}</p>
                <Link href={step.cta.href} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "var(--gold-deep)", textDecoration: "none" }}>{step.cta.label} {'>'}</Link>
              </div>
            </div>
          ))}
        </div>
        <div className="reveal-scale" style={{ textAlign: "center", marginTop: 64 }}>
          <Link href="/services" className="btn-navy" style={{ fontSize: 15 }}>Find a verified conveyancer</Link>
        </div>
      </div>

    </div>
  )
}
